import React, {useEffect, useMemo} from 'react';
import {View, FlatList, StyleSheet, TouchableOpacity, Image} from 'react-native';
import {Text, Appbar, Avatar, Divider} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {colors, spacing} from '../../theme';

const SelectItemScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const params = route.params;
  const {catalogItems, fetchCatalogItems} = useMeasurementStore();

  useEffect(() => {
    fetchCatalogItems();
  }, [fetchCatalogItems]);

  const customerGender = params.customerGender;

  const {suggested, others} = useMemo(() => {
    const sugg = [];
    const oth = [];
    catalogItems.forEach(dt => {
      const applicable = dt.gender || 'BOTH';
      if (
        applicable === 'BOTH' ||
        (customerGender === 'MALE' && applicable === 'MALE') ||
        (customerGender === 'FEMALE' && applicable === 'FEMALE')
      ) {
        sugg.push(dt);
      } else {
        oth.push(dt);
      }
    });
    return {suggested: sugg, others: oth};
  }, [catalogItems, customerGender]);

  const handleSelect = dt => {
    navigation.navigate('OrderLineDetail', {
      ...params,
      catalogItemId: dt.id,
      catalogItemName: dt.name,
    });
  };

  const renderItem = ({item}) => (
    <TouchableOpacity onPress={() => handleSelect(item)} activeOpacity={0.7}>
      <View style={styles.row}>
        {item.mainImageUrl ? (
          <View style={styles.avatarImageContainer}>
            <Image source={{uri: item.mainImageUrl}} style={styles.avatarImage} />
          </View>
        ) : (
          <Avatar.Text size={40} label={item.name.charAt(0).toUpperCase()} style={styles.avatar} />
        )}
        <Text variant="bodyLarge" style={styles.itemName}>{item.name}</Text>
        <Text style={styles.chevron}>{'>'}</Text>
      </View>
      <Divider />
    </TouchableOpacity>
  );

  const sections = [];
  if (suggested.length > 0) {
    sections.push({title: 'Suggested Items', data: suggested});
  }
  if (others.length > 0) {
    sections.push({title: 'All Items', data: others});
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title="Select Item" titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <FlatList
        data={[
          ...(suggested.length > 0 ? [{type: 'header', title: 'Suggested Items'}] : []),
          ...suggested.map(d => ({type: 'item', ...d})),
          ...(others.length > 0 ? [{type: 'header', title: 'All Items'}] : []),
          ...others.map(d => ({type: 'item', ...d})),
        ]}
        keyExtractor={(item, idx) => item.type === 'header' ? `h-${idx}` : String(item.id)}
        renderItem={({item}) => {
          if (item.type === 'header') {
            return (
              <Text variant="titleSmall" style={styles.sectionTitle}>{item.title}</Text>
            );
          }
          return renderItem({item});
        }}
        contentContainerStyle={styles.listContent}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  listContent: {paddingBottom: spacing.xxl},
  sectionTitle: {
    fontWeight: 'bold', color: colors.textSecondary,
    paddingHorizontal: spacing.md, paddingTop: spacing.md, paddingBottom: spacing.sm,
    backgroundColor: colors.background,
  },
  row: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
  },
  avatar: {backgroundColor: colors.primary + '30', marginRight: spacing.md},
  avatarImageContainer: {
    width: 40, height: 40, borderRadius: 20, marginRight: spacing.md,
    padding: 4, justifyContent: 'center', alignItems: 'center',
    borderWidth: 1, borderColor: colors.border,
  },
  avatarImage: {width: 32, height: 32, borderRadius: 16},
  itemName: {flex: 1, color: colors.text},
  chevron: {color: colors.textSecondary, fontSize: 18},
});

export default SelectItemScreen;
